import { useState, useEffect } from 'react'
import { FiX, FiMonitor, FiInfo } from 'react-icons/fi'
import api from '../services/api'
import { useAuth } from '../contexts/AuthContext'

/*
 * ActivarQuioscoModal — solo visible para admins. Lee los centros de trabajo
 * de la empresa para que el admin elija con cuál asociar la tablet, guarda
 * los flags en localStorage y redirige a /quiosco. Tras activar, cierra la
 * sesión del admin para que la tablet no quede con JWT vivo.
 *
 * Selector de centro obligatorio según spec acordada en HANDOFF (Punto Q).
 */
export default function ActivarQuioscoModal({ onClose }) {
  const { empresaInfo, logout } = useAuth()
  const [centros, setCentros] = useState([])
  const [centroId, setCentroId] = useState('')
  const [cargandoCentros, setCargandoCentros] = useState(true)
  const [activando, setActivando] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    api.get('/centros-trabajo')
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data : (res.data?.centros || [])
        setCentros(data)
      })
      .catch(() => setError('No se pudieron cargar los centros de trabajo'))
      .finally(() => setCargandoCentros(false))
  }, [])

  const onActivar = async () => {
    if (!centroId) {
      setError('Selecciona un centro de trabajo')
      return
    }
    setActivando(true)
    const centro = centros.find((c) => String(c.id) === String(centroId))

    localStorage.setItem('kiosk_mode', 'true')
    localStorage.setItem('kiosk_empresa_id', String(empresaInfo?.id || ''))
    localStorage.setItem('kiosk_empresa_nombre', empresaInfo?.nombre || '')
    localStorage.setItem('kiosk_centro_id', String(centroId))
    localStorage.setItem('kiosk_centro_nombre', centro?.nombre || '')

    // Cerrar sesión del admin: no queremos token vivo en la tablet.
    try {
      await logout()
    } catch {
      /* best-effort */
    }
    // logout() ya navega a /login; KioskGuard verá kiosk_mode=true y
    // redirigirá a /quiosco. Hacemos replace por si logout no llegó a navegar.
    window.location.replace('/quiosco')
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50">
          <div className="flex items-center gap-3">
            <FiMonitor className="w-6 h-6 text-blue-600" />
            <h2 className="text-lg font-bold text-gray-900">Activar modo quiosco</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Cerrar">
            <FiX className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-900 flex gap-3">
            <FiInfo className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium mb-1">Este dispositivo quedará bloqueado en modo quiosco.</p>
              <p>
                Los empleados podrán fichar entrada, pausa o salida solo introduciendo su PIN.
                Para volver a la app normal, mantén pulsado el logo durante 5 segundos e introduce
                tus credenciales de administrador.
              </p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Centro de trabajo asociado <span className="text-red-500">*</span>
            </label>
            <select
              value={centroId}
              onChange={(e) => setCentroId(e.target.value)}
              disabled={cargandoCentros || activando}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">
                {cargandoCentros ? 'Cargando centros…' : '— Selecciona un centro —'}
              </option>
              {centros.map((c) => (
                <option key={c.id} value={c.id}>{c.nombre}</option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Los fichajes hechos desde este quiosco quedarán marcados con este centro.
            </p>
          </div>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={activando}
              className="flex-1 py-2.5 px-4 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 font-medium disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={onActivar}
              disabled={activando || cargandoCentros}
              className="flex-1 py-2.5 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium disabled:opacity-50"
            >
              {activando ? 'Activando…' : 'Activar quiosco'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
